// @ts-check

// Vendor imports
const Immutable = require('immutable')

// Type definitions
/** @typedef { import('immutable').Record.Factory<any> } ImmutableRecordFactory */
/** @typedef { import('./types').ImmutableReviverKey } ImmutableReviverKey */
/** @typedef { import('./types').ImmutableReviverSeq } ImmutableReviverSeq */
/** @typedef { import('./types').ImmutableReviverPath } ImmutableReviverPath */
/** @typedef { import('./types').ImmutableReviver } ImmutableReviver */
/** @typedef { import('./types').ImmutableKeyedReviverFactory } ImmutableKeyedReviverFactory */
/** @typedef { import('./types').ImmutableSeqReviverFactory } ImmutableSeqReviverFactory */

/**
 * @param {ImmutableReviverKey} _key
 * @param {ImmutableReviverSeq} seq
 */
function defaultReviver(_key, seq) {
  return Immutable.isKeyed(seq)
  ? Immutable.Map(seq)
  : Immutable.List(seq)
}

/**
 * @param {ImmutableReviver} reviver
 * @returns {ImmutableReviver}
 */
function nestedReviver(reviver) {
  return function (key, seq, keyPath) {
    if (keyPath.length === 1) {
      return reviver.call(this, '', seq, [])
    }
    return reviver.call(this, key, seq, keyPath.slice(1))
  }
}

/**
 * @param {ImmutableKeyedReviverFactory | ImmutableRecordFactory} keyedFactory
 * @param {Object<string, ImmutableReviver>} reviversByKey
 * @return {ImmutableReviver}
 */
function keyedReviver(keyedFactory, reviversByKey) {
  /** @type {Object<string, ImmutableReviver>} */
  const nestedReviversByKey = {}
  for (let key of Object.keys(reviversByKey)) {
    nestedReviversByKey[key] = nestedReviver(reviversByKey[key])
  }
  return function (key, seq, keyPath) {
    if (keyPath.length === 0) {
      // Immutable.Record.Factory must be instantiated with `new`
      if (Immutable.Record.isRecord(keyedFactory.prototype)) {
        // @ts-ignore Immutable.Record.Factory type lacks a construct signature
        return new keyedFactory(seq)
      }
      return keyedFactory(seq)
    }
    const reviver = nestedReviversByKey[keyPath[0]]
    if (reviver) {
      return reviver.call(this, key, seq, keyPath)
    }
    return defaultReviver.call(this, key, seq, keyPath)
  }
}

/**
 * @param {ImmutableSeqReviverFactory} seqFactory
 * @param {ImmutableReviver} itemReviver
 * @returns {ImmutableReviver}
 */
function seqReviver(seqFactory, itemReviver) {
  const reviver = nestedReviver(itemReviver)
  return function (key, seq, keyPath) {
    if (keyPath.length === 0) {
      return seqFactory(seq)
    }
    return reviver.call(this, key, seq, keyPath)
  }
}

module.exports = {
  defaultReviver,
  keyedReviver,
  seqReviver,
  listReviver: seqReviver.bind(null, Immutable.List),
  mapReviver: seqReviver.bind(null, Immutable.Map)
}
